import { fetchUtils, DataProvider } from 'react-admin';
import queryString from 'query-string';
import { agentService } from './agent';
import { chatbotService } from './chatbot';
import { envs, getEnv } from '@/config/envs';
import { persistStorageService } from '@/features/auth/persist-storage';
import { OrgLocation } from '@/features/organizations/organizations.types';
import { authService } from '@/features/auth/auth.service';
import { IPageInfo } from '@/features/conversations/conversations.types';

/**
 * Maps react-admin queries to the API format
 * @param {string|(() => string)} apiUrl Base API URL or function that returns it (for dynamic URLs)
 * @returns {DataProvider} The data provider
 */
export const createDataProvider = (apiUrl: string | (() => string)): DataProvider => {
  // Helper to get the current API URL (supports both static and dynamic)
  const getApiUrl = (): string => {
    if (typeof apiUrl === 'function') {
      return apiUrl();
    }
    return apiUrl;
  };
  // Track retry attempts per request to avoid infinite loops
  const retryTracker = new Map<string, number>();

  // Create a fetchJson wrapper with the API key header
  const fetchJson = async (url: string, options: fetchUtils.Options = {}) => {
    if (!options.headers) {
      options.headers = new Headers({ Accept: 'application/json' });
    }

    // Add JWT token from localStorage (set by authProvider)
    const token = persistStorageService.getPersistItem<string>({
      name: 'token',
    });
    if (token) {
      (options.headers as Headers).set('Authorization', `Bearer ${token}`);
    }

    // Add API key from localStorage first, then fall back to environment variable
    const apiKey = persistStorageService.getPersistItem<string>({
      name: 'apiKey',
    }) || envs.VITE_X_API_KEY;

    if (apiKey) {
      (options.headers as Headers).set('x-api-key', apiKey);
    }

    try {
      return await fetchUtils.fetchJson(url, options);
    } catch (error) {
      // Handle 401 errors with automatic token refresh
      if (
        error &&
        typeof error === 'object' &&
        'status' in error &&
        error.status === 401
      ) {
        const refreshToken = persistStorageService.getPersistItem<string>({
          name: 'refresh_token',
        });

        // Only attempt refresh if we have a refresh token and haven't already retried twice
        if (refreshToken) {
          // Create a unique key for retry tracking
          const requestKey = `${options.method || 'GET'}:${url}`;
          const retryCount = retryTracker.get(requestKey) || 0;

          if (retryCount < 3) {
            console.log(
              `[DATA PROVIDER] 401 error detected, attempting token refresh (attempt ${retryCount + 1}/2)`,
            );

            try {
              // Attempt to refresh the token
              const refreshResult = await authService.refreshToken();

              if (refreshResult && refreshResult.token) {
                // Update retry count
                retryTracker.set(requestKey, retryCount + 1);

                // Update the Authorization header with the new token
                (options.headers as Headers).set(
                  'Authorization',
                  `Bearer ${refreshResult.token}`,
                );

                // Retry the original request
                return await fetchUtils.fetchJson(url, options);
              }
            } catch (refreshError) {
              console.error(
                '[DATA PROVIDER] Token refresh failed:',
                refreshError,
              );
            }
          } else {
            console.log(
              '[DATA PROVIDER] Max retry attempts reached, redirecting to login',
            );
          }
        }

        // If refresh failed or no refresh token, clear storage and redirect
        persistStorageService.batchRemovePersistItems({
          names: ['token', 'refresh_token', 'user', 'expires_at'],
        });
        throw error;
      }

      // Re-throw other errors with more details
      if (error && typeof error === 'object' && 'json' in error) {
        const errorDetails = (error as any).json;
        console.error('[DATA PROVIDER] Error fetching JSON:', {
          status: (error as any).status,
          statusText: (error as any).statusText,
          url,
          errorDetails,
        });
      } else {
        console.error('[DATA PROVIDER] Error fetching JSON:', error);
      }
      throw error;
    }
  };

  // Initialize hooks with the apiUrl getter and fetchJson function
  const agentHook = agentService({ apiUrl: getApiUrl(), fetchJson });
  const chatbotHook = chatbotService({ apiUrl: getApiUrl(), fetchJson });

  // Helper to get selected organization and location from localStorage
  const getOrgLocationFilters = () => {
    const filters: Record<string, any> = {};

    try {
      const orgJson = persistStorageService.getPersistItem<OrgLocation>({
        name: 'selectedOrganisation',
        parse: true,
      });
      const locJson = persistStorageService.getPersistItem<OrgLocation>({
        name: 'selectedLocation',
        parse: true,
      });

      if (orgJson) {
        const org = orgJson;
        filters.organisation_id = org.id;
      }

      if (locJson) {
        const loc = locJson;
        filters.location_id = loc.id;
      }
    } catch (e) {
      console.error('Error parsing organization/location from localStorage', e);
    }

    return filters;
  };

  // Helper to apply org/location filters to queries
  const applyOrgLocationFilters = (filter: any = {}) => {
    // Get filters from localStorage
    const orgLocFilters = getOrgLocationFilters();

    // Skip applying filters for certain resources
    const skipOrgFilter = [
      'organisations',
      'roles',
      'auth-types',
      'users',
      'agents',
    ];
    const skipLocationFilter = [
      'organisations',
      'locations',
      'roles',
      'auth-types',
      'users',
      'agents',
      'resources',
    ];

    const currentResource = window.location.pathname.split('/')[1] || '';

    // Create a new filter object
    const enhancedFilter = { ...filter };

    // Apply organization filter if appropriate
    if (
      orgLocFilters.organisation_id &&
      !skipOrgFilter.includes(currentResource)
    ) {
      enhancedFilter.organisation_id = orgLocFilters.organisation_id;
    }

    if (
      orgLocFilters.location_id &&
      !skipLocationFilter.includes(currentResource)
    ) {
      enhancedFilter.location_id = orgLocFilters.location_id;
    }

    return enhancedFilter;
  };

  // Also update the default create implementation
  const defaultCreate = async (resource: string, params: any) => {
    const url = `${getApiUrl()}/api/v1/${resource}`;
    const options = {
      method: 'POST',
      body: JSON.stringify({ data: params.data }),
    };

    const { json } = await fetchJson(url, options);

    return {
      data: json.data || {},
    };
  };

  // And update the default update implementation
  const defaultUpdate = async (resource: string, params: any) => {
    const url = `${getApiUrl()}/api/v1/${resource}/${params.id}`;
    const options = {
      method: 'PUT',
      body: JSON.stringify({ data: params.data }),
    };

    const { json } = await fetchJson(url, options);

    return {
      data: json.data || {},
    };
  };

  // This is workaround to mark-messages-read unisn dataProvider because need a new update cunction
  const updateConversationRead = async (params: any) => {
    const url = `${getApiUrl()}/api/v1/conversations/${params.id}/mark-messages-read`;
    const options = {
      method: 'PUT',
      body: JSON.stringify({ data: params.data }),
    };

    const { json } = await fetchJson(url, options);

    return {
      data: json.data || {},
    };
  };

  return {
    getList: async (resource, params) => {
      // Ensure pagination and sort are defined with default values if not provided
      const pagination = params.pagination || { page: 1, perPage: 10 };
      const sort = params.sort || { field: 'id', order: 'ASC' };
      // Apply org/location filters
      const enhancedFilter = applyOrgLocationFilters(params.filter);

      if (resource === 'resources' || resource === 'chatbots') {
        delete enhancedFilter.location_id;
      }

      const query = {
        sort: sort.field,
        order: sort.order,
        page: pagination.page,
        perPage: pagination.perPage,
        filter: JSON.stringify(enhancedFilter),
      };

      const url = `${getApiUrl()}/api/v1/${resource}?${queryString.stringify(query)}`;
      const { json } = await fetchJson(url);

      return {
        data: json.data || [],
        total: json.total || json.data?.length || 0,
        pageInfo: json.pageInfo as IPageInfo,
      };
    },

    getOne: async (resource, params) => {
      if (resource === 'agents') {
        return agentHook.handleAgentGetOne(params);
      }
      if (resource === 'chatbots') {
        return chatbotHook.handleChatbotGetOne(params);
      }
      if (resource === 'chatbots_settings') {
        return chatbotHook.handleChatbotSettingsGetOne(params);
      }

      // Add a debug log to see what's being requested
      console.log(
        `Default getOne request for resource: ${resource}, id: ${params.id}`,
      );

      // Use explicit resource name for the URL, don't rely on the params.resource
      const url = `${getApiUrl()}/api/v1/${resource}/${params.id}`;

      try {
        const { json } = await fetchJson(url);
        // Basic validation for default getOne
        if (!json.data || typeof json.data.id === 'undefined') {
          console.warn(
            `Default getOne for ${resource}/${params.id} did not return data with an id.`,
          );
          // Return a minimal structure to satisfy react-admin's expectation
          return { data: { id: params.id } };
        }
        return {
          data: json.data,
        };
      } catch (error) {
        console.error(
          `Error in default getOne for ${resource}/${params.id}:`,
          error,
        );
        // Return a minimal structure on error
        return { data: { id: params.id } };
      }
    },

    getMany: async (resource, params) => {
      // Apply org/location filters to the 'ids' based filter
      const enhancedFilter = {
        ids: params.ids,
        ...getOrgLocationFilters(),
      };

      const query = {
        filter: JSON.stringify(enhancedFilter),
      };

      // Add a debug log
      // console.log(`getMany request for resource: ${resource}, ids: ${params.ids}`);

      // Use explicit resource name for the URL
      const url = `${getApiUrl()}/api/v1/${resource}?${queryString.stringify(query)}`;
      const { json } = await fetchJson(url);

      return {
        data: json.data || [],
        pageInfo: json.pageInfo as IPageInfo,
      };
    },

    getManyReference: async (resource, params) => {
      // Ensure pagination and sort are defined with default values if not provided
      const pagination = params.pagination || { page: 1, perPage: 10 };
      const sort = params.sort || { field: 'id', order: 'ASC' };

      // Apply target field filter and org/location filters
      const filter = { ...params.filter };
      filter[params.target] = params.id;

      const enhancedFilter = applyOrgLocationFilters(filter);

      const query = {
        sort: sort.field,
        order: sort.order,
        page: pagination.page,
        perPage: pagination.perPage,
        filter: JSON.stringify(enhancedFilter),
      };

      const url = `${getApiUrl()}/api/v1/${resource}?${queryString.stringify(query)}`;
      const { json } = await fetchJson(url);

      return {
        data: json.data || [],
        total: json.total || json.data?.length || 0,
        pageInfo: json.pageInfo as IPageInfo,
      };
    },

    create: async (resource, params) => {
      if (resource === 'agents') {
        return agentHook.handleAgentCreate(params);
      }

      // Default implementation for other resources
      return defaultCreate(resource, params);
    },

    update: async (resource, params) => {
      if (resource === 'agents') {
        return agentHook.handleAgentUpdate(params);
      }
      if (resource === 'chatbots') {
        return chatbotHook.handleChatbotUpdate(params);
      }
      if (resource === 'chatbots_settings') {
        return chatbotHook.handleChatbotSettingsUpdate(params);
      }

      if (resource.includes('mark-messages-read')) {
        return updateConversationRead(params);
      }
      // Default implementation for other resources
      return defaultUpdate(resource, params);
    },

    updateMany: async (resource, params) => {
      // Apply org/location filters to the updateMany operation
      const enhancedFilter = {
        ids: params.ids,
        ...getOrgLocationFilters(),
      };

      const query = {
        filter: JSON.stringify(enhancedFilter),
      };

      const url = `${getApiUrl()}/api/v1/${resource}?${queryString.stringify(query)}`;
      const options = {
        method: 'PUT',
        body: JSON.stringify({ data: params.data }),
      };

      await fetchJson(url, options);

      return {
        data: params.ids,
      };
    },

    delete: async (resource, params) => {
      const url = `${getApiUrl()}/api/v1/${resource}/${params.id}`;
      const options = {
        method: 'DELETE',
      };

      const { json } = await fetchJson(url, options);

      return {
        data: json.data || {},
      };
    },

    deleteMany: async (resource, params) => {
      // Apply org/location filters to the deleteMany operation
      const enhancedFilter = {
        ids: params.ids,
        ...getOrgLocationFilters(),
      };

      const query = {
        filter: JSON.stringify(enhancedFilter),
      };

      const url = `${getApiUrl()}/api/v1/${resource}?${queryString.stringify(query)}`;
      const options = {
        method: 'DELETE',
      };

      await fetchJson(url, options);

      return {
        data: params.ids,
      };
    },
  };
};

// Use getEnv() function to get runtime API URL dynamically (supports widget context)
// Priority order:
// 1. Widget config apiHost (runtime, set via ConversationsWidget.init({ apiHost: '...' }))
// 2. Build-time VITE_API_URL (from Docker build args or .env file)
// 3. Empty string (fallback)
export const dataProvider = createDataProvider(() => {
  const env = getEnv();
  // getEnv() checks window.__WIDGET_ENV__ first (widget config), then import.meta.env (build-time)
  const apiUrl = env.VITE_API_URL || envs.VITE_API_URL || '';
  
  // Debug logging (can be removed in production)
  if (typeof window !== 'undefined') {
    const fromWidget = typeof window.__WIDGET_ENV__ !== 'undefined' && window.__WIDGET_ENV__?.VITE_API_URL;
    const fromBuildTime = !fromWidget && envs.VITE_API_URL;
    console.log('[DataProvider] API URL source:', {
      url: apiUrl,
      source: fromWidget ? 'widget-config' : fromBuildTime ? 'build-time' : 'none',
    });
  }
  
  return apiUrl;
});
